import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { companyApi, formatApiError } from '../services/api.ts';

const CATEGORY_OPTIONS = [
  '5M INC（控股）',
  '4P INC（控股）',
  '4P LLC（營運）',
  '4P INC（營運）',
  '控股（其他）',
  '營運',
  'EHJS',
];

export default function CompanyForm() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    entity_id: '',
    state: '',
    entity_jurisdiction: '',
    entity_type: '',
    category: '',
    nickname: '',
    company_name: '',
    registration_date: '',
    formation_document_date: '',
    ein_letter: '',
    federal_tax_id: '',
    sos_id: '',
    boir_id: '',
    duns: '',
    status: '',
    irs_registrant: '',
    sos_registrant: '',
    next_file: '',
    sos_filing_date: '',
    owner_member: '',
    ceo_manager: '',
    cfo_tax_rep: '',
    cfo: '',
    tax_rep: '',
    secretary: '',
    director: '',
    principal_address: '',
    mailing_address: '',
    business_description: '',
    registered_agent_name: '',
    registered_agent_address: '',
    naics_code: '',
    sic_code: '',
    termination_date: '',
    employee_count: '',
    website: '',
    username: '',
    password: '',
    question: '',
    six_month: '',
    five_month: '',
    cn_4p: '',
    group: '',
    has_us_staff: '',
    remark: '',
    payment: '',
    sos_renewal_comment: '',
    // internal fields (not in UI but needed for API compatibility)
    ceo_manager_role: '',
    ceo_manager_role_override: false,
    naics_description: '',
    sic_description: '',
  });

  // Entity category detection for role display
  // US LLC: 'LLC' → shows Manager, Member, Tax Rep
  // US INC: 'INC' → shows CEO, CFO, Secretary, Director, Shareholder
  // HK/MY: 'LIMITED', 'S-LIMITED BY SHARES' → shows Director, Shareholder
  const getEntityCategory = () => {
    const et = (formData.entity_type || '').toUpperCase().trim();
    if (et === 'LLC' || et === 'LP') return 'US_LLC';
    if (et === 'INC' || et === 'CORPORATION' || et === 'CORP') return 'US_INC';
    if (et === 'LIMITED' || et === 'S-LIMITED BY SHARES' || et === 'S-LIMITED') return 'HK_MY';
    return 'US_LLC';
  };

  const entityCategory = getEntityCategory();

  useEffect(() => {
    if (isEdit && id) {
      fetchCompany(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchCompany = async (companyId: number) => {
    try {
      const response = await companyApi.getById(companyId);
      const c = response.data;
      setFormData({
        entity_id: c.entity_id || '',
        state: c.state || '',
        entity_jurisdiction: c.entity_jurisdiction || '',
        entity_type: c.entity_type || '',
        category: c.category || '',
        nickname: c.nickname || '',
        company_name: c.company_name || '',
        registration_date: c.registration_date || '',
        formation_document_date: c.formation_document_date || '',
        ein_letter: c.ein_letter || '',
        federal_tax_id: c.federal_tax_id || '',
        sos_id: c.sos_id || '',
        boir_id: c.boir_id || '',
        duns: c.duns || '',
        status: c.status || '',
        irs_registrant: c.irs_registrant || '',
        sos_registrant: c.sos_registrant || '',
        next_file: c.next_file || '',
        sos_filing_date: c.sos_filing_date || '',
        owner_member: c.owner_member || '',
        ceo_manager: c.ceo_manager || '',
        cfo_tax_rep: c.cfo_tax_rep || '',
        cfo: c.cfo || '',
        tax_rep: c.tax_rep || '',
        secretary: c.secretary || '',
        director: c.director || '',
        principal_address: c.principal_address || '',
        mailing_address: c.mailing_address || '',
        business_description: c.business_description || '',
        registered_agent_name: c.registered_agent_name || '',
        registered_agent_address: c.registered_agent_address || '',
        naics_code: c.naics_code || '',
        sic_code: c.sic_code || '',
        termination_date: c.termination_date || '',
        employee_count: c.employee_count || '',
        website: c.website || '',
        username: c.username || '',
        password: c.password || '',
        question: c.question || '',
        six_month: c.six_month || '',
        five_month: c.five_month || '',
        cn_4p: c.cn_4p || '',
        group: c.group_name || c.group || '',
        has_us_staff: c.has_us_staff || '',
        remark: c.remark || '',
        payment: c.payment || '',
        sos_renewal_comment: c.sos_renewal_comment || '',
        ceo_manager_role: c.ceo_manager_role || '',
        ceo_manager_role_override: c.ceo_manager_role_override || false,
        naics_description: c.naics_description || '',
        sic_description: c.sic_description || '',
      });
    } catch (error) {
      console.error('Failed to fetch company:', error);
    }
  };

  const goBack = () => {
    const stateFrom = location.state?.from as string | undefined;
    if (stateFrom) {
      navigate(stateFrom);
      return;
    }
    const fromPage = sessionStorage.getItem('companyList_fromPage');
    if (fromPage) {
      sessionStorage.removeItem('companyList_fromPage');
      navigate(fromPage);
    } else {
      navigate(-1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validation: Termination Date is required when Status = Terminated
    if (formData.status === 'Terminated' && !formData.termination_date) {
      alert('Please fill in Termination Date when Status is Terminated.');
      return;
    }
    // Note: termination_date is preserved even when status != Terminated.
    // The field is only hidden in UI, data is never deleted.
    try {
      if (isEdit && id) {
        await companyApi.update(parseInt(id), formData);
      } else {
        await companyApi.create(formData);
      }
      goBack();
    } catch (error) {
      const info = formatApiError(error, isEdit ? 'Update Company' : 'Create Company');
      alert(`${info.label}\n\n${info.message}`);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      
      // Auto-detect entity_type from company_name
      if (name === 'company_name') {
        const nameUpper = value.toUpperCase();
        let detectedJurisdiction = '';
        let detectedType = '';

        if (/\bLLC\b/.test(nameUpper)) { detectedType = 'LLC'; }
        else if (/\bINC\.?\b/.test(nameUpper)) { detectedType = 'INC'; }
        else if (/\bLIMITED\b|\bLTD\.?\b/.test(nameUpper)) { detectedType = 'LIMITED'; }
        else if (/\bS[-]?LIMITED\b/.test(nameUpper)) { detectedType = 'S-LIMITED BY SHARES'; }
        else if (/\bCORPORATION\b|\bCORP\.?\b/.test(nameUpper)) { detectedType = 'INC'; }
        else if (/\bLP\b/.test(nameUpper)) { detectedType = 'LLC'; }

        if (detectedType) next.entity_type = detectedType;
      }
      
      return next;
    });
  };

  const getDefaultRole = (type: string) => {
    if (!type) return 'MANAGER';
    const upperType = type.toUpperCase();
    if (upperType === 'INC') return 'CEO';
    if (upperType === 'LIMITED' || upperType === 'S-LIMITED BY SHARES') return 'DIRECTOR';
    return 'MANAGER';
  };

  // Auto-set ceo_manager_role and split legacy cfo_tax_rep based on entity_type
  useEffect(() => {
    const autoRole = getDefaultRole(formData.entity_type);
    if (!formData.ceo_manager_role_override && formData.ceo_manager_role !== autoRole) {
      setFormData(prev => ({ ...prev, ceo_manager_role: autoRole }));
    }
    
    // Auto-split legacy CFO/TAX REP into separate fields
    if (formData.cfo_tax_rep) {
      const et = (formData.entity_type || '').toUpperCase();
      if (et === 'LLC' && !formData.tax_rep) {
        setFormData(prev => ({ ...prev, tax_rep: prev.cfo_tax_rep }));
      } else if (et === 'INC' && !formData.cfo) {
        setFormData(prev => ({ ...prev, cfo: prev.cfo_tax_rep }));
      }
    }
  }, [formData.entity_type]); // eslint-disable-line react-hooks/exhaustive-deps

  const inputCls = "w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
  const sectionCls = "text-lg font-semibold text-slate-800 mb-4 pb-2 border-b border-slate-200";

  return (
    <div className="fade-in max-w-6xl mx-auto">
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={goBack}
          className="flex items-center space-x-2 px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('action.back')}</span>
        </button>
        <h1 className="text-2xl font-bold text-slate-800">
          {isEdit ? t('company.editCompany') : t('company.newCompany')}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="grid grid-cols-3 gap-6">

          {/* === Section 1: Basic Information === */}
          <div className="col-span-3">
            <h3 className={sectionCls}>Basic Information</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
            <input type="text" name="state" value={formData.state} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Entity Jurisdiction</label>
            <select name="entity_jurisdiction" value={formData.entity_jurisdiction} onChange={handleChange} className={inputCls}>
              <option value="">-</option>
              <option value="Domestic">Domestic</option>
              <option value="Foreign">Foreign</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Entity Type</label>
            <select name="entity_type" value={formData.entity_type} onChange={handleChange} className={inputCls}>
              <option value="">-</option>
              <option value="LLC">LLC</option>
              <option value="INC">INC</option>
              <option value="LIMITED">LIMITED</option>
              <option value="S-LIMITED BY SHARES">S-LIMITED BY SHARES</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Entity ID</label>
            <input type="text" name="entity_id" value={formData.entity_id} onChange={handleChange} placeholder="Unique entity identifier (auto-generated if empty)" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
            <select name="category" value={formData.category} onChange={handleChange} className={inputCls}>
              <option value="">Select category</option>
              {CATEGORY_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
              {/* Allow custom values if stored data doesn't match presets */}
              {formData.category && !CATEGORY_OPTIONS.includes(formData.category) && (
                <option value={formData.category}>{formData.category}</option>
              )}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nickname</label>
            <input type="text" name="nickname" value={formData.nickname} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Company Name *</label>
            <input type="text" name="company_name" value={formData.company_name} onChange={handleChange} autoComplete="off" required className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Remark</label>
            <textarea name="remark" value={formData.remark} onChange={handleChange} rows={2} className={inputCls} placeholder="備註" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Registration Date</label>
            <input type="date" name="registration_date" value={formData.registration_date} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select name="status" value={formData.status} onChange={handleChange} className={inputCls}>
              <option value="">-</option>
              <option value="Active">Active</option>
              <option value="Converted">Converted</option>
              <option value="Terminated">Terminated</option>
              <option value="Applying">Applying</option>
              <option value="待Close">待Close</option>
              <option value="註冊一半">註冊一半</option>
              <option value="待調整">待調整</option>
              <option value="HOLD">HOLD</option>
              <option value="當年註冊">當年註冊</option>
            </select>
          </div>
          {/* Termination Date only shows when Status = Terminated */}
          {(formData.status === 'Terminated' || (formData.termination_date && isEdit)) && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Termination Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                name="termination_date"
                value={formData.termination_date}
                onChange={handleChange}
                className={`${inputCls} ${!formData.termination_date ? 'border-red-400 focus:ring-red-400' : ''}`}
                required
              />
              {!formData.termination_date && (
                <p className="text-xs text-red-500 mt-1">Required when Status is Terminated</p>
              )}
            </div>
          )}

          {/* === Section 2: Tax & IDs === */}
          <div className="col-span-3 mt-6">
            <h3 className={sectionCls}>Tax & IDs</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Formation Document Date</label>
            <input type="date" name="formation_document_date" value={formData.formation_document_date} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">EIN Letter</label>
            <select name="ein_letter" value={formData.ein_letter} onChange={handleChange} className={inputCls}>
              <option value="">Select EIN Letter</option>
              <option value="CP 575">CP 575</option>
              <option value="147C">147C</option>
              <option value="-">-</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Federal Tax ID</label>
            <input type="text" name="federal_tax_id" value={formData.federal_tax_id} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">SOS ID</label>
            <input type="text" name="sos_id" value={formData.sos_id} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">BOIR ID（BOI）</label>
            <input type="text" name="boir_id" value={formData.boir_id} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">DUNS#</label>
            <input type="text" name="duns" value={formData.duns} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>

          {/* === Section 3: SOS & IRS Filing === */}
          <div className="col-span-3 mt-6">
            <h3 className={sectionCls}>SOS & IRS Filing</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">IRS Registrant</label>
            <input type="text" name="irs_registrant" value={formData.irs_registrant} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">SOS Registrant</label>
            <input type="text" name="sos_registrant" value={formData.sos_registrant} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Next File</label>
            <input type="text" name="next_file" value={formData.next_file} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">SOS Filing Date</label>
            <input type="text" name="sos_filing_date" value={formData.sos_filing_date} onChange={handleChange} autoComplete="off" className={inputCls} placeholder="e.g. 每年01/01-02/28" />
          </div>
          <div className="col-span-3">
            <label className="block text-sm font-medium text-slate-700 mb-1">Payment</label>
            <select name="payment" value={formData.payment} onChange={handleChange} className={inputCls}>
              <option value="">Select Payment</option>
              <option value="Master/Visa">Master/Visa</option>
              <option value="Master/Visa + Fee">Master/Visa + Fee</option>
              <option value="Amex">Amex</option>
              <option value="Amex + Fee">Amex + Fee</option>
              <option value="eCheck">eCheck</option>
              <option value="eCheck + Fee">eCheck + Fee</option>
              <option value="Check">Check</option>
              <option value="-">-</option>
            </select>
          </div>
          <div className="col-span-3">
            <label className="block text-sm font-medium text-slate-700 mb-1">SOS Renewal Comment</label>
            <textarea name="sos_renewal_comment" value={formData.sos_renewal_comment} onChange={handleChange} rows={3} className={`${inputCls} resize-none`} placeholder="Enter SOS renewal comment"></textarea>
          </div>

          {/* === Section 4: Management (conditional based on Entity Category) === */}
          <div className="col-span-3 mt-6">
            <h3 className={sectionCls}>
              Management
              <span className="ml-2 text-sm font-normal text-slate-500">
                ({entityCategory === 'US_LLC' ? 'US LLC' : entityCategory === 'US_INC' ? 'US INC' : 'HK / Malaysia'})
              </span>
            </h3>
          </div>

          {/* US LLC: Manager, Member, Tax Rep */}
          {entityCategory === 'US_LLC' && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">MANAGER</label>
                <input type="text" name="ceo_manager" value={formData.ceo_manager} onChange={handleChange} autoComplete="off" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">MEMBER</label>
                <input type="text" name="owner_member" value={formData.owner_member} onChange={handleChange} autoComplete="off" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">TAX REP.</label>
                <input type="text" name="tax_rep" value={formData.tax_rep} onChange={handleChange} autoComplete="off" className={inputCls} placeholder="Enter TAX REP name" />
              </div>
            </>
          )}

          {/* US INC: CEO, CFO, Secretary, Director, Shareholder */}
          {entityCategory === 'US_INC' && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">CEO</label>
                <input type="text" name="ceo_manager" value={formData.ceo_manager} onChange={handleChange} autoComplete="off" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">CFO</label>
                <input type="text" name="cfo" value={formData.cfo} onChange={handleChange} autoComplete="off" className={inputCls} placeholder="Enter CFO name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">SECRETARY</label>
                <input type="text" name="secretary" value={formData.secretary} onChange={handleChange} autoComplete="off" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">DIRECTOR</label>
                <input type="text" name="director" value={formData.director} onChange={handleChange} autoComplete="off" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">SHAREHOLDER</label>
                <input type="text" name="owner_member" value={formData.owner_member} onChange={handleChange} autoComplete="off" className={inputCls} />
              </div>
            </>
          )}

          {/* HK/MY: Director, Shareholder */}
          {entityCategory === 'HK_MY' && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">DIRECTOR</label>
                <input type="text" name="director" value={formData.director} onChange={handleChange} autoComplete="off" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">SHAREHOLDER</label>
                <input type="text" name="owner_member" value={formData.owner_member} onChange={handleChange} autoComplete="off" className={inputCls} />
              </div>
            </>
          )}

          {/* === Section 5: Addresses === */}
          <div className="col-span-3 mt-6">
            <h3 className={sectionCls}>Addresses</h3>
          </div>

          <div className="col-span-3">
            <label className="block text-sm font-medium text-slate-700 mb-1">Principal Address</label>
            <textarea name="principal_address" value={formData.principal_address} onChange={handleChange} rows={2} className={`${inputCls} resize-none`}></textarea>
          </div>
          <div className="col-span-3">
            <label className="block text-sm font-medium text-slate-700 mb-1">Mailing Address</label>
            <textarea name="mailing_address" value={formData.mailing_address} onChange={handleChange} rows={2} className={`${inputCls} resize-none`}></textarea>
          </div>
          <div className="col-span-3">
            <label className="block text-sm font-medium text-slate-700 mb-1">Business Description</label>
            <textarea name="business_description" value={formData.business_description} onChange={handleChange} rows={3} className={`${inputCls} resize-none`}></textarea>
          </div>

          {/* === Section 6: Registered Agent === */}
          <div className="col-span-3 mt-6">
            <h3 className={sectionCls}>Registered Agent</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Registered Agent Name</label>
            <input type="text" name="registered_agent_name" value={formData.registered_agent_name} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Registered Agent Address</label>
            <textarea name="registered_agent_address" value={formData.registered_agent_address} onChange={handleChange} rows={2} className={`${inputCls} resize-none`}></textarea>
          </div>

          {/* === Section 7: Business Codes === */}
          <div className="col-span-3 mt-6">
            <h3 className={sectionCls}>Business Codes</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">NAICS Code</label>
            <input type="text" name="naics_code" value={formData.naics_code} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">NAICS Description</label>
            <textarea
              name="naics_description"
              value={formData.naics_description || ''}
              onChange={handleChange}
              rows={2}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none resize-none ${
                formData.naics_description?.includes('Need Review') ? 'border-amber-300 text-amber-700' :
                formData.naics_description?.includes('Not Found') ? 'border-red-300 text-red-500 italic' :
                'border-slate-200 text-slate-700'
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">SIC Code</label>
            <input type="text" name="sic_code" value={formData.sic_code} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">SIC Description</label>
            <textarea
              name="sic_description"
              value={formData.sic_description || ''}
              onChange={handleChange}
              rows={2}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none resize-none ${
                formData.sic_description?.includes('Need Review') ? 'border-amber-300 text-amber-700' :
                formData.sic_description?.includes('Not Found') ? 'border-red-300 text-red-500 italic' :
                'border-slate-200 text-slate-700'
              }`}
            />
          </div>

          {/* === Section 8: Online & Credentials === */}
          <div className="col-span-3 mt-6">
            <h3 className={sectionCls}>Online & Credentials</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">員工人數</label>
            <select name="employee_count" value={formData.employee_count} onChange={handleChange} className={inputCls}>
              <option value="">Select 員工人數</option>
              <option value="0人 - 控股公司INC">0人 - 控股公司INC</option>
              <option value="0人 - 營運INC（1人公司1099）">0人 - 營運INC（1人公司1099）</option>
              <option value="0人 - 營運LLC（1人公司1099）">0人 - 營運LLC（1人公司1099）</option>
              <option value="1人 - 營運LLC（1人公司W2）">1人 - 營運LLC（1人公司W2）</option>
              <option value="N人 - 營運LLC（多人公司W2+1099）">N人 - 營運LLC（多人公司W2+1099）</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Website</label>
            <input type="text" name="website" value={formData.website} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
            <input type="text" name="username" value={formData.username} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
            <input type="text" name="password" value={formData.password} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Question</label>
            <input type="text" name="question" value={formData.question} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>

          {/* === Section 9: Filing Reminders & Groups === */}
          <div className="col-span-3 mt-6">
            <h3 className={sectionCls}>Filing Reminders & Groups</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">6M</label>
            <input type="text" name="six_month" value={formData.six_month} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">5M</label>
            <input type="text" name="five_month" value={formData.five_month} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">CN 4P</label>
            <input type="text" name="cn_4p" value={formData.cn_4p} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">GROUP</label>
            <input type="text" name="group" value={formData.group} onChange={handleChange} autoComplete="off" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">是否有US現場人員</label>
            <select name="has_us_staff" value={formData.has_us_staff} onChange={handleChange} className={inputCls}>
              <option value="">-</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>

          {/* === Submit === */}
          <div className="col-span-3 mt-6 pt-6 border-t border-slate-200">
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={goBack}
                className="px-6 py-2.5 text-sm font-medium border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                {t('action.cancel')}
              </button>
              <button
                type="submit"
                className="px-6 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{t('action.save')}</span>
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
